import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MessageCircle } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { SeasonBadge, SeasonCalendar } from "@/components/site/SeasonCalendar";
import { Skeleton } from "@/components/ui/skeleton";
import { useProduct } from "@/lib/useProducts";
import { seasonStatus, WHATSAPP_URL } from "@/lib/site";
import NotFound from "@/pages/NotFound";

const SpecTable = ({ p }) => (
  <table className="w-full text-sm" data-testid="product-specs">
    <tbody>
      {[["Variety", p.variety], ["Origin", p.origin], ...p.specs.map((s) => [s.label, s.value]),
        ["MOQ", p.moq], ["Incoterms", p.incoterms.join(" / ")], ["Port of loading", p.ports.join(", ")]].map(([k, v]) => (
        <tr key={k} className="border-b border-stone-200 last:border-0">
          <th className="py-3 pr-6 text-left font-normal text-stone-500 w-1/2">{k}</th>
          <td className="py-3 font-medium text-stone-900">{v}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const PackingList = ({ items }) => (
  <div className="grid sm:grid-cols-2 gap-4" data-testid="product-packing">
    {items.map((k, i) => (
      <div key={i} className="rounded-lg border border-stone-200 bg-white p-6 space-y-2">
        <p className="font-display text-2xl font-semibold text-emerald-900">{k.net_weight}</p>
        <p className="text-sm text-stone-700">{k.type}</p>
        <p className="text-xs text-stone-500">{k.per_container}</p>
      </div>
    ))}
  </div>
);

const Gallery = ({ p }) => {
  const photos = p.gallery?.length ? p.gallery : [{ src: p.image, caption: p.name }];
  const [active, setActive] = useState(0);
  const cur = photos[active] || photos[0];
  return (
    <div className="space-y-3" data-testid="product-gallery">
      <img src={cur.src} alt={cur.caption || p.name} className="rounded-lg w-full aspect-[4/3] object-cover" data-testid="product-main-image" />
      {photos.length > 1 && (
        <div className="grid grid-cols-3 gap-3">
          {photos.map((ph, i) => (
            <button key={ph.src} onClick={() => setActive(i)} data-testid={`product-thumb-${i}`}
              className={`text-left space-y-1.5 rounded-md transition-opacity ${i === active ? "" : "opacity-70 hover:opacity-100"}`}>
              <img src={ph.src} alt={ph.caption} loading="lazy"
                className={`w-full aspect-[4/3] object-cover rounded-md ${i === active ? "ring-2 ring-emerald-700 ring-offset-2" : ""}`} />
              <span className="block text-xs text-stone-500">{ph.caption}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function ProductDetail() {
  const { slug } = useParams();
  const { data: p, isLoading, isError } = useProduct(slug);
  if (isLoading) return <div className="max-w-7xl mx-auto px-6 py-24"><Skeleton className="h-[480px] rounded-lg" /></div>;
  if (isError || !p) return <NotFound />;
  const status = seasonStatus(p);
  return (
    <>
      <Seo title={`${p.name} exporter from India`} description={`${p.name} (${p.variety}) export specs, packing, MOQ ${p.moq}, ${p.incoterms.join("/")}. ${p.short}`} path={`/products/${p.slug}`} />
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <Link to="/products" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-emerald-900 mb-10" data-testid="product-back-link">
          <ArrowLeft className="h-4 w-4" /> All products
        </Link>
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <Gallery p={p} />
          </div>
          <div className="md:col-span-6 space-y-6">
            <SeasonBadge status={status} testid="product-season-status" />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight" data-testid="product-name">{p.name}</h1>
            <p className="text-stone-600 leading-relaxed">{p.description}</p>
            <div className="flex flex-wrap gap-3">
              <Link to={`/request-quote?product=${p.slug}`} data-testid="product-quote-btn" className="rounded-full bg-amber-700 hover:bg-amber-800 text-white px-7 py-3.5 font-medium transition-colors">Request a quote</Link>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="product-whatsapp-btn" className="inline-flex items-center gap-2 rounded-full border border-stone-300 hover:bg-white px-6 py-3.5 font-medium transition-colors">
                <MessageCircle className="h-4 w-4 text-green-600" /> Ask on WhatsApp
              </a>
            </div>
            {p.is_sample && <p className="text-xs text-stone-400" data-testid="product-sample-note">Typical export specs shown. Final grade and packing are confirmed in your quote.</p>}
          </div>
        </div>
        {p.show_calendar && (
          <div className="mt-20 space-y-5" data-testid="product-season-section">
            <h2 className="text-2xl lg:text-3xl font-semibold tracking-tight">Season calendar</h2>
            <SeasonCalendar months={p.available_months} />
            <p className="text-xs text-stone-500">Green months are shipping months. The outlined month is the current month.</p>
          </div>
        )}
        <div className="mt-20 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-6 space-y-5">
            <h2 className="text-2xl lg:text-3xl font-semibold tracking-tight">Specifications</h2>
            <SpecTable p={p} />
          </div>
          <div className="md:col-span-6 space-y-5">
            <h2 className="text-2xl lg:text-3xl font-semibold tracking-tight">Packing options</h2>
            <PackingList items={p.packing} />
          </div>
        </div>
      </section>
    </>
  );
}
